import React, { useState } from "react";
import {
  Box,
  CircularProgress,
  Alert,
  Chip,
  Link,
  IconButton,
  Button,
  MenuItem,
  Menu,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Dialog,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import ReactCountryFlag from "react-country-flag";
import LaunchIcon from "@mui/icons-material/Launch";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import StarIcon from "@mui/icons-material/Star";
import { RecruitWithPlayer } from "../../../types/RosterTypes";
import { useSeason } from "../../../contexts/SeasonContext";
import { usePersonnel } from "../../../hooks/usePersonnel";
import { useRoster } from "../../../contexts/RosterContext";
import { RecruitProfileModal } from "./RecruitProfileModal";

export const Recruits = () => {
  const personnelController = usePersonnel();
  const { fetchRosterPlayers, selectedRoster } = useRoster();
  const { selectedSeason } = useSeason();
  const [selectedRecruit, setSelectedRecruit] =
    useState<RecruitWithPlayer | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [savingRows, setSavingRows] = useState<Record<string, boolean>>({});

  // Action menu state
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const [currentRecruitId, setCurrentRecruitId] = useState<number | null>(null);

  // Popover states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [addToRosterDialogOpen, setAddToRosterDialogOpen] = useState(false);
  const [rateDialogOpen, setRateDialogOpen] = useState(false);

  // Profile modal state
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [isNewRecruit, setIsNewRecruit] = useState(false);

  // Player profile modal state
  const [newPlayer, setNewPlayer] = useState<any>({});
  const [newMembership, setNewMembership] = useState<any>({ status: "ACTIVE" });

  // Generate graduation year options (current year to current year + 8)
  const currentYear = new Date().getFullYear();
  const gradYearOptions = Array.from({ length: 9 }, (_, i) =>
    (currentYear + i).toString()
  );

  // Add this near your other state declarations
  const [error, setError] = useState<string | null>(null);

  // Get current recruit
  const getCurrentRecruit = () => {
    if (currentRecruitId === null) return null;
    return personnelController.recruits.find((recruit) => recruit.id === currentRecruitId) || null;
  };

  // Action menu handlers
  const handleOpenActionMenu = (
    event: React.MouseEvent<HTMLElement>,
    recruitId: number
  ) => {
    event.stopPropagation(); // Prevent row click
    setActionMenuAnchor(event.currentTarget);
    setCurrentRecruitId(recruitId);
  };

  const handleCloseActionMenu = () => {
    setActionMenuAnchor(null);
  };

  // Delete handlers
  const handleOpenDeleteDialog = () => {
    handleCloseActionMenu();
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  const handleConfirmDelete = async () => {
    if (currentRecruitId === null) return;

    try {
      await personnelController.archiveRecruit(currentRecruitId);
      // After archiving, close the dialog
      handleCloseDeleteDialog();
    } catch (error) {
      console.error("Failed to archive recruit:", error);
    }
  };

  // View details handler
  const handleViewDetails = () => {
    handleCloseActionMenu();
    setIsNewRecruit(false);
    setProfileModalOpen(true);
  };

  // Add new recruit handler
  const handleAddNewRecruit = () => {
    setIsNewRecruit(true);
    setCurrentRecruitId(null);
    setProfileModalOpen(true);
  };

  // Add to roster handlers
  const handleOpenAddToRoster = () => {
    handleCloseActionMenu();
    setAddToRosterDialogOpen(true);
  };

  const handleCloseAddToRoster = () => {
    setAddToRosterDialogOpen(false);
  };

  // Rate handlers
  const handleOpenRateDialog = () => {
    handleCloseActionMenu();
    setRateDialogOpen(true);
  };

  const handleCloseRateDialog = () => {
    setRateDialogOpen(false);
  };

  // Get priority level color
  const getPriorityColor = (priority: string | undefined) => {
    switch (priority) {
      case "High":
        return "error";
      case "Medium":
        return "warning";
      case "Low":
        return "info";
      default:
        return "default";
    }
  };

  // Get application status color
  const getApplicationStatusColor = (status: string | null) => {
    if (!status) return "default";
    switch (status) {
      case "COMPLETED":
        return "success";
      case "PENDING":
        return "warning";
      case "INQUIRY_SUBMITTED":
        return "info";
      case "NOT_APPLIED":
        return "default";
      default:
        return "default";
    }
  };

  // Get financial aid color
  const getFinancialAidColor = (status: string | null) => {
    if (!status) return "default";
    switch (status) {
      case "HIGH":
        return "success";
      case "MEDIUM":
        return "warning";
      case "LOW":
        return "error";
      case "UNKNOWN":
        return "default";
      default:
        return "default";
    }
  };

  // Format position group for display
  const formatPositionGroup = (value: string | null | undefined) => {
    if (!value) return "";

    switch (value) {
      case "SKILL":
        return "Skill";
      case "BIG_SKILL":
        return "Big Skill";
      case "BIG":
        return "Big";
      default:
        return value;
    }
  };

  // Map display values back to DB values
  const getPositionGroupValue = (displayValue: string): string => {
    switch (displayValue) {
      case "Skill":
        return "SKILL";
      case "Big Skill":
        return "BIG_SKILL";
      case "Big":
        return "BIG";
      default:
        return displayValue;
    }
  };

  const columns: GridColDef[] = [
    {
      field: "actions",
      headerName: "Actions",
      width: 70,
      headerAlign: "center",
      align: "center",
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={(event) => handleOpenActionMenu(event, params.row.id)}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    },
    {
      field: "name",
      headerName: "Name",
      width: 200,
      filterable: true,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {params.row.country_code && (
            <ReactCountryFlag
              countryCode={params.row.country_code}
              svg
              style={{
                width: "1.2em",
                height: "1.2em",
              }}
              title={params.row.country_code}
            />
          )}
          <span>{params.value || ""}</span>
        </Box>
      ),
    },
    {
      field: "off_pos",
      headerName: "Offense",
      width: 120,
      headerAlign: "center",
      align: "center",
      filterable: true,
      editable: true,
      type: "singleSelect",
      valueOptions: ["OL", "RB", "TE", "QB", "WR"],
    },
    {
      field: "def_pos",
      headerName: "Defense",
      width: 120,
      headerAlign: "center",
      align: "center",
      filterable: true,
      editable: true,
      type: "singleSelect",
      valueOptions: ["DL", "LB", "DB"],
    },
    {
      field: "positionGroup",
      headerName: "Position Group",
      width: 150,
      headerAlign: "center",
      align: "center",
      filterable: true,
      editable: true,
      type: "singleSelect",
      valueOptions: ["Skill", "Big Skill", "Big"],
      renderCell: (params: any) => formatPositionGroup(params.value),
    },
    {
      field: "height",
      headerName: "Height",
      width: 100,
      headerAlign: "center",
      align: "center",
      editable: true,
      type: "singleSelect",
      valueOptions: Array.from({ length: 17 }, (_, i) => {
        const feet = Math.floor((i + 66) / 12);
        const inches = (i + 66) % 12;
        return `${feet}'${inches}"`;
      }),
      renderCell: (params) => {
        if (params.value === null || params.value === undefined) return "-";
        const inches = params.value as number;
        return `${Math.floor(inches / 12)}'${inches % 12}"`;
      },
    },
    {
      field: "weight",
      headerName: "Weight",
      width: 100,
      headerAlign: "center",
      align: "center",
      editable: true,
      type: "number",
    },
    {
      field: "gpa",
      headerName: "GPA",
      width: 80,
      headerAlign: "center",
      align: "center",
      editable: true,
      type: "number",
      renderCell: (params) => {
        return params.value !== null ? params.value : "";
      },
    },
    {
      field: "gradYear",
      headerName: "Grad Year",
      width: 120,
      headerAlign: "center",
      align: "center",
      filterable: true,
      editable: true,
      type: "singleSelect",
      valueOptions: gradYearOptions,
    },
    {
      field: "priority",
      headerName: "Priority",
      width: 120,
      headerAlign: "center",
      align: "center",
      filterable: true,
      editable: true,
      renderCell: (params) => (
        <Chip
          label={params.value || "None"}
          color={getPriorityColor(params.value) as any}
          size="small"
        />
      ),
      type: "singleSelect",
      valueOptions: ["High", "Medium", "Low", "None"],
    },
    {
      field: "applicationStatus",
      headerName: "Application",
      width: 150,
      headerAlign: "center",
      align: "center",
      filterable: true,
      editable: true,
      type: "singleSelect",
      valueOptions: [
        "COMPLETED",
        "PENDING",
        "INQUIRY_SUBMITTED",
        "NOT_APPLIED",
      ],
      renderCell: (params) => {
        if (!params.value)
          return <Chip label="None" color="default" size="small" />;
        return (
          <Chip
            label={params.value.replace("_", " ")}
            color={getApplicationStatusColor(params.value) as any}
            size="small"
          />
        );
      },
    },
    {
      field: "financialAid",
      headerName: "Financial Aid",
      width: 150,
      headerAlign: "center",
      align: "center",
      filterable: true,
      editable: true,
      type: "singleSelect",
      valueOptions: ["HIGH", "MEDIUM", "LOW", "UNKNOWN"],
      renderCell: (params) => {
        if (!params.value)
          return <Chip label="Unknown" color="default" size="small" />;
        return (
          <Chip
            label={params.value}
            color={getFinancialAidColor(params.value) as any}
            size="small"
          />
        );
      },
    },
    {
      field: "budget",
      headerName: "Budget",
      width: 120,
      headerAlign: "center",
      align: "center",
      editable: true,
      renderCell: (params) => {
        const id = params.id.toString();

        // Show spinner if this cell's row is being saved
        if (savingRows[id]) {
          return <CircularProgress size={16} />;
        }

        return params.value !== null && params.value !== undefined
          ? `$${params.value.toLocaleString()}`
          : "-";
      },
    },
    {
      field: "agent",
      headerName: "Agent",
      width: 150,
      headerAlign: "center",
      align: "center",
      filterable: true,
      editable: true,
    },
    {
      field: "filmLink",
      headerName: "Film",
      width: 80,
      headerAlign: "center",
      align: "center",
      editable: true,
      renderCell: (params) =>
        params.value ? (
          <Link href={params.value} target="_blank" rel="noopener noreferrer">
            <LaunchIcon fontSize="small" />
          </Link>
        ) : (
          <span>-</span>
        ),
    },
  ];

  // Transform recruits data into rows for DataGrid
  const rows = personnelController.recruits.map((recruit) => {
    return {
      id: recruit.id,
      name: recruit.player.name,
      country_code: recruit.player.country_code,
      off_pos: recruit.player.offensive_position,
      def_pos: recruit.player.defensive_position,
      positionGroup: recruit.player.position_group,
      height: recruit.player.height,
      weight: recruit.player.weight,
      gpa: recruit.player.gpa,
      gradYear: recruit.player.grad_year,
      currentTeam: recruit.current_team,
      priority: recruit.priority,
      applicationStatus: recruit.application_status,
      financialAid: recruit.financial_aid,
      faAppStatus: recruit.fa_app_status,
      filmLink: recruit.film_link,
      city: recruit.player.city,
      state: recruit.player.state,
      agent: recruit.agent,
      budget: recruit.budget,
      notes: recruit.recruiting_notes,
    };
  });

  // Handle recruit row click
  const handleRecruitClick = (params: any) => {
    const selectedRecruitData = personnelController.recruits.find(
      (recruit) => recruit.id === params.id
    );

    if (selectedRecruitData) {
      setSelectedRecruit(selectedRecruitData);
      // Here you could open a modal with details, similar to the PlayerProfileModal in Roster.tsx
    }
  };

  // Handle adding a new recruit or updating an existing one
  const handleSaveRecruit = async (recruitData: any) => {
    try {
      console.log("Saving recruit:", recruitData);
      await personnelController.createOrUpdateRecruit(recruitData);

      // Refresh data if needed - but createOrUpdateRecruit already calls fetchRecruits
    } catch (error) {
      console.error("Failed to save recruit:", error);
    }
  };

  const tableWidth = columns.reduce(
    (acc, column) => acc + (column?.width || 0),
    0
  );

  // Update the handleAddToRoster function
  const handleAddToRoster = async () => {
    const recruit = getCurrentRecruit();
    if (!recruit || !selectedRoster?.id) {
      console.error("Missing recruit or roster data");
      return;
    }

    try {
      // Create roster membership
      const membershipData = {
        player: recruit.player.id,
        roster: selectedRoster.id,
      };

      await personnelController.addRecruitToRoster(membershipData);
      
      handleCloseAddToRoster();

      // Refresh the roster data
      if (selectedRoster?.id) {
        await fetchRosterPlayers(selectedRoster.id);
      }

      // Show success message (you'll need to implement this)
      // You could use a snackbar or other notification system
      console.log("Successfully added recruit to roster");

    } catch (error: any) {
      console.error("Error adding recruit to roster:", error);
      // Handle error (show error message to user)
      setError(error.response?.data?.detail || "Failed to add recruit to roster");
    }
  };

  return (
    <Box sx={{ 
      p: 1, 
      maxWidth: tableWidth + 40, 
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column"
    }}>
      {/* Header with buttons */}
      <Box
        sx={{
          mb: 2,
          display: "flex",
          gap: 2,
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          {/* Add Recruit button */}
          <IconButton
            onClick={handleAddNewRecruit}
            color="primary"
            title="Add Recruit"
            size="small"
          >
            <AddIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleCloseActionMenu}
      >
        <MenuItem onClick={handleViewDetails} sx={{ gap: 1 }}>
          <VisibilityIcon fontSize="small" />
          View/Edit Details
        </MenuItem>
        <MenuItem onClick={handleOpenAddToRoster} sx={{ gap: 1 }}>
          <PersonAddIcon fontSize="small" />
          Add to Roster
        </MenuItem>
        <MenuItem onClick={handleOpenRateDialog} sx={{ gap: 1 }}>
          <StarIcon fontSize="small" />
          Rate
        </MenuItem>
        <MenuItem
          onClick={handleOpenDeleteDialog}
          sx={{ gap: 1, color: "error.main" }}
        >
          <DeleteIcon fontSize="small" />
          Archive
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Archive Recruit</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to archive this recruit? Archived recruits
            will no longer appear in the main list.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            Archive
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add to Roster Dialog */}
      <Dialog
        open={addToRosterDialogOpen}
        onClose={handleCloseAddToRoster}
        aria-labelledby="add-to-roster-dialog"
        aria-describedby="dialog-to-add-recruit-to-roster"
      >
        <DialogTitle>Add to Roster</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <DialogContentText>
            You are adding{" "}
            <strong>{getCurrentRecruit()?.player.name}</strong> to the{" "}
            <strong>
              {selectedSeason?.name} {selectedRoster?.name}
            </strong>
            {" "}roster
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddToRoster}>Cancel</Button>
          <Button
            onClick={handleAddToRoster}
            variant="contained"
            color="primary"
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rate Dialog */}
      <Dialog
        open={rateDialogOpen}
        onClose={handleCloseRateDialog}
        aria-labelledby="rate-dialog"
        aria-describedby="dialog-to-rate-recruit"
      >
        <DialogTitle>Rate Recruit</DialogTitle>
        <DialogContent>
          <DialogContentText>Rate the player here.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRateDialog}>Cancel</Button>
          <Button
            onClick={() => {
              // Handle rating submission
              handleCloseRateDialog();
            }}
            variant="contained"
            color="primary"
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Recruit Profile Modal */}
      <RecruitProfileModal
        open={profileModalOpen}
        onClose={() => setProfileModalOpen(false)}
        recruit={getCurrentRecruit()}
        isNewRecruit={isNewRecruit}
        onSave={handleSaveRecruit}
      />

      {/* Loading and Error States */}
      {personnelController.isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {personnelController.error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {personnelController.error}
        </Alert>
      )}

      {/* Recruits Table using DataGrid */}
      {!personnelController.isLoading && !personnelController.error && personnelController.recruits.length > 0 && (
        <>
          <Box sx={{ width: "100%", flex: 1, overflow: "hidden" }}>
            <DataGrid
              rows={rows}
              columns={columns}
              initialState={{
                sorting: {
                  sortModel: [{ field: "priority", sort: "asc" }],
                },
                columns: {
                  columnVisibilityModel: {
                    // Hide some columns by default to focus on the most important ones
                    gpa: true,
                    city: false,
                    state: false,
                  },
                },
              }}
              hideFooter
              disableRowSelectionOnClick
              onRowClick={handleRecruitClick}
              disableColumnMenu={false}
              density="compact"
              editMode="cell"
              onCellEditStop={(params, event) => {
                // Handle validation if needed
              }}
              processRowUpdate={(updatedRow, originalRow) => {
                // Find what field changed
                const changedFields = Object.keys(updatedRow).filter(
                  (key) => updatedRow[key] !== originalRow[key]
                );

                if (changedFields.length > 0) {
                  const rowId = updatedRow.id.toString();

                  // Show spinner for this row while saving
                  setSavingRows((prev) => ({ ...prev, [rowId]: true }));

                  // Create an object with just the changed fields
                  const changedValues = changedFields.reduce((acc, field) => {
                    acc[field] = updatedRow[field];
                    return acc;
                  }, {} as Record<string, any>);

                  console.log(
                    "Saving changes:",
                    rowId,
                    changedFields,
                    changedValues
                  );

                  // Save immediately to API
                  personnelController
                    .createOrUpdateRecruit({
                      id: Number(rowId),
                      ...changedValues,
                    })
                    .then(() => {
                      console.log("Successfully saved changes for row", rowId);
                      // Remove spinner when done
                      setSavingRows((prev) => {
                        const newState = { ...prev };
                        delete newState[rowId];
                        return newState;
                      });
                    })
                    .catch((error) => {
                      console.error("Error saving changes:", error);
                      // Remove spinner on error
                      setSavingRows((prev) => {
                        const newState = { ...prev };
                        delete newState[rowId];
                        return newState;
                      });
                      // You could add error handling/notification here
                    });
                }

                return updatedRow;
              }}
              onProcessRowUpdateError={(error) => {
                console.error("Error updating row:", error);
              }}
              sx={{
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "primary.main",
                  color: "white",
                  fontWeight: "bold",
                  position: "sticky",
                  top: 0,
                  zIndex: 5,
                },
                "& .MuiDataGrid-row:hover": {
                  cursor: "pointer",
                  backgroundColor: "rgba(170, 163, 163, 0.39)",
                },
                "& .MuiDataGrid-cell:last-of-type": {
                  paddingRight: "8px",
                },
                // Make column headers more compact
                "& .MuiDataGrid-columnHeader": {
                  padding: "0 8px",
                },
                height: "100%",
                width: "100%",
                border: "none",
                // Allow scrolling within the table
                "& .MuiDataGrid-main": {
                  overflow: "auto",
                },
                "& .MuiDataGrid-virtualScroller": {
                  overflow: "auto",
                  "&::-webkit-scrollbar": {
                    width: "8px",
                    height: "8px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "rgba(0,0,0,.2)",
                    borderRadius: "4px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "rgba(0,0,0,.05)",
                  },
                },
              }}
            />
          </Box>
        </>
      )}

      {!personnelController.isLoading && !personnelController.error && personnelController.recruits.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No recruits found.
        </Alert>
      )}
    </Box>
  );
};
